import { useEffect, useCallback } from 'react';
import api from '../utils/api';

// Default keyboard shortcuts configuration
export const DEFAULT_SHORTCUTS = {
  // Global shortcuts
  newTask: { key: 'n', ctrl: true, description: 'Create new task', category: 'Global' },
  quickSearch: { key: 'k', ctrl: true, description: 'Open quick search', category: 'Global' },
  showHelp: { key: '?', shift: true, description: 'Show keyboard shortcuts', category: 'Global' },
  closeModal: { key: 'Escape', description: 'Close modal / Go back', category: 'Global' },
  
  // Navigation shortcuts
  goToDashboard: { key: 'd', ctrl: true, shift: true, description: 'Go to Dashboard', category: 'Navigation' },
  goToTaskHub: { key: 't', ctrl: true, shift: true, description: 'Go to Task Hub', category: 'Navigation' },
  
  // Task modal shortcuts
  saveTask: { key: 's', ctrl: true, description: 'Save task', category: 'Task Modal' },
  saveAndClose: { key: 'Enter', ctrl: true, description: 'Save and close task', category: 'Task Modal' },
  markComplete: { key: 'c', ctrl: true, shift: true, description: 'Mark task complete', category: 'Task Modal' },
};

// Storage key for user's custom shortcuts (localStorage fallback)
const SHORTCUTS_STORAGE_KEY = 'athena_keyboard_shortcuts';

// In-memory cache to avoid repeated API calls
let shortcutsCache = null;
let cacheTimestamp = null;
const CACHE_TTL = 60000; // 1 minute cache

// Get shortcuts from cache, localStorage, or defaults
export const getShortcuts = () => {
  try {
    // First check in-memory cache
    if (shortcutsCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_TTL)) {
      return shortcutsCache;
    }
    
    // Then check localStorage
    const stored = localStorage.getItem(SHORTCUTS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Deep merge with defaults to ensure all properties exist
      const merged = {};
      for (const [key, defaultShortcut] of Object.entries(DEFAULT_SHORTCUTS)) {
        const savedShortcut = parsed[key];
        if (savedShortcut && typeof savedShortcut === 'object') {
          // Merge saved shortcut with default to ensure all properties exist
          merged[key] = { ...defaultShortcut, ...savedShortcut };
        } else {
          merged[key] = defaultShortcut;
        }
      }
      shortcutsCache = merged;
      cacheTimestamp = Date.now();
      return merged;
    }
  } catch (e) {
    console.error('Failed to load keyboard shortcuts:', e);
  }
  return DEFAULT_SHORTCUTS;
};

// Fetch shortcuts from backend API
export const fetchShortcutsFromServer = async () => {
  try {
    const response = await api.get('/users/keyboard-shortcuts');
    if (response.data?.success && response.data?.keyboard_shortcuts) {
      const serverShortcuts = response.data.keyboard_shortcuts;
      
      // Deep merge with defaults to ensure all properties exist
      const merged = {};
      for (const [key, defaultShortcut] of Object.entries(DEFAULT_SHORTCUTS)) {
        const savedShortcut = serverShortcuts[key];
        if (savedShortcut && typeof savedShortcut === 'object') {
          merged[key] = { ...defaultShortcut, ...savedShortcut };
        } else {
          merged[key] = defaultShortcut;
        }
      }
      
      // Update localStorage and cache
      localStorage.setItem(SHORTCUTS_STORAGE_KEY, JSON.stringify(merged));
      shortcutsCache = merged;
      cacheTimestamp = Date.now();
      
      console.log('✅ Synced keyboard shortcuts from server');
      return merged;
    }
    // No custom shortcuts on server, use defaults
    return DEFAULT_SHORTCUTS;
  } catch (error) {
    console.warn('Could not fetch keyboard shortcuts from server, using local:', error);
    return getShortcuts();
  }
};

// Save custom shortcuts to localStorage AND backend
export const saveShortcuts = async (shortcuts) => {
  try {
    // Save to localStorage immediately for responsiveness
    localStorage.setItem(SHORTCUTS_STORAGE_KEY, JSON.stringify(shortcuts));
    shortcutsCache = shortcuts;
    cacheTimestamp = Date.now();
    console.log('💾 Saved shortcuts to localStorage');
    
    // Sync to backend (don't await - fire and forget for better UX)
    api.put('/users/keyboard-shortcuts', { keyboard_shortcuts: shortcuts })
      .then(() => {
        console.log('✅ Synced keyboard shortcuts to server');
      })
      .catch((error) => {
        console.warn('Could not sync keyboard shortcuts to server:', error);
      });
    
    return true;
  } catch (e) {
    console.error('❌ Failed to save keyboard shortcuts:', e);
    return false;
  }
};

// Reset to default shortcuts (localStorage AND backend)
export const resetShortcuts = async () => {
  try {
    localStorage.removeItem(SHORTCUTS_STORAGE_KEY);
    shortcutsCache = null;
    cacheTimestamp = null;
    
    // Sync reset to backend
    api.delete('/users/keyboard-shortcuts')
      .then(() => {
        console.log('✅ Reset keyboard shortcuts on server');
      })
      .catch((error) => {
        console.warn('Could not reset keyboard shortcuts on server:', error);
      });
    
    return DEFAULT_SHORTCUTS;
  } catch (e) {
    console.error('Failed to reset shortcuts:', e);
    return DEFAULT_SHORTCUTS;
  }
};

// Initialize shortcuts - call this after login to sync from server
export const initializeShortcuts = async () => {
  try {
    const serverShortcuts = await fetchShortcutsFromServer();
    return serverShortcuts;
  } catch (error) {
    console.warn('Failed to initialize shortcuts from server:', error);
    return getShortcuts();
  }
};

// Format shortcut for display
export const formatShortcut = (shortcut) => {
  if (!shortcut) return '';
  const parts = [];
  if (shortcut.ctrl) parts.push('Ctrl');
  if (shortcut.alt) parts.push('Alt');
  if (shortcut.shift) parts.push('Shift');
  
  let key = shortcut.key;
  // Format special keys
  if (key === 'Escape') key = 'Esc';
  if (key === 'ArrowLeft') key = '←';
  if (key === 'ArrowRight') key = '→';
  if (key === 'ArrowUp') key = '↑';
  if (key === 'ArrowDown') key = '↓';
  if (key === 'Enter') key = '↵';
  if (key === ' ') key = 'Space';
  if (key.length === 1) key = key.toUpperCase();
  
  parts.push(key);
  return parts.join(' + ');
};

// Check if an element is an input field
const isInputElement = (element) => {
  if (!element) return false;
  const tagName = element.tagName?.toLowerCase();
  const isContentEditable = element.isContentEditable;
  return tagName === 'input' || tagName === 'textarea' || tagName === 'select' || isContentEditable;
};

// Check if a keyboard event matches a shortcut
const matchesShortcut = (event, shortcut) => {
  if (!shortcut) return false;
  
  const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase() ||
                   event.key === shortcut.key;
  const ctrlMatch = !!shortcut.ctrl === (event.ctrlKey || event.metaKey);
  const altMatch = !!shortcut.alt === event.altKey;
  const shiftMatch = !!shortcut.shift === event.shiftKey;
  
  return keyMatch && ctrlMatch && altMatch && shiftMatch;
};

/**
 * Hook for handling keyboard shortcuts
 * @param {Object} handlers - Object mapping shortcut names to handler functions
 * @param {Object} options - Options for the hook
 * @param {boolean} options.enabled - Whether shortcuts are enabled (default: true)
 * @param {boolean} options.allowInInputs - Whether to allow shortcuts in input fields (default: false)
 */
export const useKeyboardShortcuts = (handlers = {}, options = {}) => {
  const { enabled = true, allowInInputs = false } = options;
  
  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;
    
    // Skip if in input field (unless allowed)
    if (!allowInInputs && isInputElement(event.target)) {
      // Always allow Escape in inputs
      if (event.key !== 'Escape') return;
    }
    
    const shortcuts = getShortcuts();
    
    // Check each handler's shortcut
    for (const [name, handler] of Object.entries(handlers)) {
      const shortcut = shortcuts[name];
      if (shortcut && matchesShortcut(event, shortcut)) {
        event.preventDefault();
        event.stopPropagation();
        handler(event);
        return;
      }
    }
  }, [handlers, enabled, allowInInputs]);
  
  useEffect(() => {
    if (enabled) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown, enabled]);
};

export default useKeyboardShortcuts;
